import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import OrderBillSender from '../../components/OrderBillSender';
import BillDetailsModal from '../../components/BillDetailsModal';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import QRCode from 'react-qr-code';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { calculateServiceCharge } from '../../utils/calculations';
import { saveDataUrlAsFile, savedFileHint } from '../../utils/saveFileDownload';
import AlertMessage from '../../components/AlertMessage';

// Load jsPDF dynamically
if (typeof window !== 'undefined' && !window.jspdf) {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.async = true;
    document.head.appendChild(script);
}

const TableStatus = () => {
    const navigate = useNavigate();
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    const { toasts, showToast, removeToast } = useToast();
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [newTableNumber, setNewTableNumber] = useState('');
    const [selectedOrderForBill, setSelectedOrderForBill] = useState(null);
    const [newOrder, setNewOrder] = useState({
        customerName: '',
        mobileNumber: '',
        items: [{ itemId: '', name: '', quantity: 1, price: 0 }],
        orderDate: '',
    });
    const [error, setError] = useState(null);
    const [modalError, setModalError] = useState('');
    const [modalSuccess, setModalSuccess] = useState('');
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    const [selectedTableNumber, setSelectedTableNumber] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [orderToEdit, setOrderToEdit] = useState(null);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [restaurantDetails, setRestaurantDetails] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [showStats, setShowStats] = useState(true);
    const [orderSearchTerm, setOrderSearchTerm] = useState('');
    const [selectedTableForQR, setSelectedTableForQR] = useState(null);
    const [qrCode, setQrCode] = useState('');
    const qrRef = useRef(null);
    const [itemSearchTerms, setItemSearchTerms] = useState({}); // Track search term for each item row
    const [openDropdownIndex, setOpenDropdownIndex] = useState(null); // Track which dropdown is open

    // Helper function to get today's date in YYYY-MM-DD format
    const getTodayString = () => new Date().toISOString().split('T')[0];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdownIndex !== null && !event.target.closest('.item-dropdown-container')) {
                setOpenDropdownIndex(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdownIndex]);

    useEffect(() => {
        const fetchData = async () => {
            if (!restaurantId) {
                setError('Restaurant ID not found');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const [tablesRes, restaurantRes, menuRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.TABLES(restaurantId)),
                    axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId)),
                    axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`)
                ]);
                setTables(tablesRes.data);
                setRestaurantDetails(restaurantRes.data);
                setMenuItems(menuRes.data);
            } catch (err) {
                setError('Failed to load data');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [restaurantId]);

    // Filter tables based on search and status
    const filteredTables = tables.filter(table => {
        const matchesSearch = table.tableNumber.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || table.status.toLowerCase() === filterStatus.toLowerCase();
        return matchesSearch && matchesStatus;
    });

    // Filter orders based on search term
    const filteredOrders = orders.filter(order => {
        if (!orderSearchTerm) return true;
        const searchLower = orderSearchTerm.toLowerCase();
        return (
            order.customerName.toLowerCase().includes(searchLower) ||
            order.mobileNumber.includes(orderSearchTerm) ||
            order.items.some(item => item.name.toLowerCase().includes(searchLower))
        );
    });

    // Helper function to check if edit/delete is allowed based on time
    const canEditOrder = (orderDate) => {
        const orderTime = new Date(orderDate);
        const currentTime = new Date();
        const timeDiff = (currentTime - orderTime) / (1000 * 60); // difference in minutes
        return timeDiff <= 30; // Allow edit within 30 minutes
    };

    const canDeleteOrder = (orderDate) => {
        const orderTime = new Date(orderDate);
        const currentTime = new Date();
        const timeDiff = (currentTime - orderTime) / (1000 * 60); // difference in minutes
        return timeDiff <= 60; // Allow delete within 1 hour
    };

    // Calculate statistics
    const stats = {
        total: tables.length,
        available: tables.filter(t => t.status === 'Available').length,
        occupied: tables.filter(t => t.status === 'Occupied').length,
        totalOrders: orders.length
    };

    const calculateTaxAmount = () => {
        const taxPercentage = restaurantDetails?.taxPercentage;
        if (taxPercentage === null || taxPercentage === undefined) {
            return '0.00';
        }
        const totalTax = ((calculateTotalAmount() * taxPercentage) / 100).toFixed(2);
        return totalTax;
    };

    // Fetch orders for a table; if no custom dates provided, default to current date
    const fetchOrdersForTable = async (tableId, tableNumber, startDate = '', endDate = '') => {
        try {
            // Default to current date if both dates are empty
            if (!startDate && !endDate) {
                const today = getTodayString();
                startDate = today;
                endDate = today;
            }
            const url = API_ENDPOINTS.ORDERS_BY_TABLE(tableId, startDate, endDate);
            const response = await axios.get(url);
            setOrders(response.data);
            setSelectedTable(tableId);
            setSelectedTableNumber(tableNumber);
            setShowOrderDetails(true);
        } catch (err) {
            setError('Failed to load orders');
            console.error(err);
        }
    };

    // When user clicks "Filter", apply the custom date filter
    const applyDateFilter = () => {
        if (!selectedTable || !selectedTableNumber) return;
        fetchOrdersForTable(selectedTable, selectedTableNumber, filterStartDate, filterEndDate);
    };

    const handleItemChange = (e, index) => {
        const updatedItems = [...newOrder.items];
        updatedItems[index] = { ...updatedItems[index], [e.target.name]: e.target.value };

        const selectedItem = menuItems.find(item => item._id === updatedItems[index].itemId);
        if (selectedItem) {
            updatedItems[index].name = selectedItem.itemName;
            updatedItems[index].price = selectedItem.itemPrice;
        }
        setNewOrder({ ...newOrder, items: updatedItems });
    };

    const addItemRow = () => {
        setNewOrder({
            ...newOrder,
            items: [...newOrder.items, { itemId: '', name: '', quantity: 1, price: 0 }],
        });
    };

    const removeItem = (index) => {
        const updatedItems = [...newOrder.items];
        updatedItems.splice(index, 1);
        setNewOrder({ ...newOrder, items: updatedItems });
        // Clean up search term for removed item
        const newSearchTerms = { ...itemSearchTerms };
        delete newSearchTerms[index];
        setItemSearchTerms(newSearchTerms);
        if (openDropdownIndex === index) {
            setOpenDropdownIndex(null);
        }
    };

    const setItemQuantity = (index, quantity) => {
        const num = Math.max(1, parseInt(quantity, 10) || 1);
        const updatedItems = [...newOrder.items];
        updatedItems[index] = { ...updatedItems[index], quantity: num };
        setNewOrder({ ...newOrder, items: updatedItems });
    };

    const handleItemSelect = (menuItem, index) => {
        const updatedItems = [...newOrder.items];
        updatedItems[index] = {
            ...updatedItems[index],
            itemId: menuItem._id,
            name: menuItem.itemName,
            price: menuItem.itemPrice
        };
        setNewOrder({ ...newOrder, items: updatedItems });
        // Clear search term and close dropdown for this row
        const newSearchTerms = { ...itemSearchTerms };
        newSearchTerms[index] = '';
        setItemSearchTerms(newSearchTerms);
        setOpenDropdownIndex(null);
    };

    const handleItemSearchChange = (value, index) => {
        const newSearchTerms = { ...itemSearchTerms };
        newSearchTerms[index] = value;
        setItemSearchTerms(newSearchTerms);
        setOpenDropdownIndex(index);
    };

    // Filter menu items based on search term for a specific row
    const getFilteredMenuItems = (index) => {
        const searchTerm = itemSearchTerms[index] || '';
        if (!searchTerm) return menuItems.filter(item => item.isAvailable);
        return menuItems.filter(item => 
            item.isAvailable && 
            item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const calculateTotalAmount = () => {
        return newOrder.items.reduce((total, item) => total + item.price * item.quantity, 0);
    };



    const deleteOrder = async (orderId) => {
        try {
            await axios.delete(API_ENDPOINTS.ORDER_BY_ID(orderId));
            setOrders(orders.filter(order => order._id !== orderId));
        } catch (err) {
            setError('Failed to delete order');
            console.error(err);
        }
    };

    const addTable = async () => {
        if (!newTableNumber) {
            setError('Table number is required');
            return;
        }
        try {
            const response = await axios.post(API_ENDPOINTS.TABLES_ADD, { tableNumber: newTableNumber, restaurantId });
            setTables([...tables, response.data]);
            setNewTableNumber('');
        } catch (err) {
            setError('Failed to add table');
            console.error(err);
        }
    };

    const toggleTableStatus = async (id) => {
        try {
            const response = await axios.put(API_ENDPOINTS.TABLE_STATUS(id), { restaurantId });
            setTables(tables.map(table => table._id === id ? response.data : table));
        } catch (err) {
            setError('Failed to update table status');
            console.error(err);
        }
    };

    const deleteTable = async (id) => {
        try {
            await axios.delete(API_ENDPOINTS.TABLE_DELETE(id), { data: { restaurantId } });
            setTables(tables.filter(table => table._id !== id));
        } catch (err) {
            setError('Failed to delete table');
            console.error(err);
        }
    };

    const closeOrderDetails = () => {
        setShowOrderDetails(false);
    };

    const handleServiceAmount = () => {
        const amount = calculateTotalAmount();
        return calculateServiceCharge(amount);
    }
    const openAddOrderModal = (tableId) => {
        setSelectedTable(tableId);
        setIsEditing(false);
        setShowOrderModal(true);
    };

    const handleEditOrder = (order) => {
        const serviceAmount = handleServiceAmount();
        setOrderToEdit(order);
        setIsEditing(true);
        setNewOrder({
            taxPercentage: order.taxPercentage,
            taxAmount: order.taxAmount,
            serviceAmount: serviceAmount,
            customerName: order.customerName,
            mobileNumber: order.mobileNumber,
            orderDate: order.orderDate ? new Date(order.orderDate).toLocaleString() : new Date().toLocaleString(),
            items: order.items.map(item => ({
                itemId: item.itemId, // Preserve original menu item ID
                name: item.name,
                quantity: item.quantity,
                price: item.price,
            })),
        });
        setShowOrderModal(true);
    };

    const closeOrderModal = () => {
        setShowOrderModal(false);
        setNewOrder({
            customerName: '',
            mobileNumber: '',
            items: [{ itemId: '', name: '', quantity: 1, price: 0 }],
            orderDate: '',
        });
        setIsEditing(false);
        setOrderToEdit(null);
        setModalError('');
        setModalSuccess('');
        setIsCreatingOrder(false);
        setIsUpdatingOrder(false);
        // Clear item search terms and close dropdowns
        setItemSearchTerms({});
        setOpenDropdownIndex(null);
    };
    const updateOrder = async () => {
        setIsUpdatingOrder(true);
        setModalError('');
        try {
            const response = await axios.put(API_ENDPOINTS.ORDER_BY_ID(orderToEdit._id), newOrder);
            const updatedOrder = response.data;
            setOrders(orders.map(order => order._id === orderToEdit._id ? updatedOrder : order));
            closeOrderModal();
        } catch (err) {
            setError('Failed to update order');
            setModalError('Failed to update order');
            console.error(err);
        } finally {
            setIsUpdatingOrder(false);
        }
    };
    const addOrder = async () => {
        // Clear previous modal errors
        setModalError('');
        setModalSuccess('');
        
        // Validate required fields with specific error messages
        if (!newOrder.customerName.trim()) {
            setModalError('⚠️ Customer name is required');
            return;
        }
        
        // Mobile number is optional, but if provided, it must be exactly 10 digits
        if (newOrder.mobileNumber.trim() && newOrder.mobileNumber.length !== 10) {
            setModalError('⚠️ Mobile number must be exactly 10 digits');
            return;
        }
        
        if (newOrder.items.some(item => !item.itemId || item.quantity <= 0)) {
            setModalError('⚠️ Please select valid items with quantity greater than 0');
            return;
        }
        
        if (newOrder.items.length === 0 || newOrder.items.every(item => !item.itemId)) {
            setModalError('⚠️ Please add at least one item to the order');
            return;
        }
        
        setIsCreatingOrder(true);
        try {
            const serviceAmount = handleServiceAmount();
            const payload = {
                tableId: selectedTable,
                restaurantId,
                serviceAmount: serviceAmount,
                ...newOrder,
            }
            const response = await axios.post(API_ENDPOINTS.ORDERS_ADD, payload);
            const createdOrder = response.data;
            setOrders([...orders, createdOrder]);
            setModalError('');
            setModalSuccess('✅ Order created successfully!');
            
            // Close modal after showing success message
            setTimeout(() => {
                closeOrderModal();
            }, 1500);
        } catch (err) {
            setModalError('Failed to add order');
            setError('Failed to add order');
            console.error(err);
        } finally {
            setIsCreatingOrder(false);
        }
    };

    const showTableQR = async (table) => {
        try {
            const response = await axios.get(API_ENDPOINTS.TABLE_QR(table._id, restaurantId));
            console.log('QR Response:', response.data);
            setQrCode(response.data.qrUrl);
            setSelectedTableForQR(table);
        } catch (err) {
            setError('Failed to generate QR code');
            console.error(err);
        }
    };

    const closeQRModal = () => {
        setSelectedTableForQR(null);
        setQrCode('');
    };
    return (
        <div className="relative min-h-[60vh] overflow-hidden">
            {/* Header — same pattern as Manage Menu */}
            <div className="sticky top-0 z-10 bg-white border-b-2 border-orange-400 shadow-sm">
                <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <h2 className="text-lg sm:text-2xl font-bold text-stone-800 truncate">Table Management</h2>
                        <span className="hidden xs:inline text-xs sm:text-sm text-stone-500 flex-shrink-0">{tables.length} tables</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => setShowStats(!showStats)}
                            className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 sm:px-4 rounded-xl font-semibold text-xs sm:text-sm min-h-[44px] transition-all ${showStats ? 'bg-orange-100 text-orange-700' : 'bg-orange-500 text-white hover:bg-orange-600 shadow-md active:scale-[0.98]'}`}
                        >
                            <span className="text-base sm:text-lg">📊</span>
                            <span className="hidden xs:inline">{showStats ? 'Hide' : 'Show'} Stats</span>
                        </button>
                        <button
                            onClick={() => navigate('/restaurant/tables-fullscreen')}
                            className="inline-flex items-center justify-center p-2.5 sm:px-4 sm:py-2.5 rounded-xl font-semibold text-sm bg-stone-100 text-stone-700 hover:bg-stone-200 active:scale-[0.98] min-h-[44px] min-w-[44px]"
                            title="Fullscreen"
                        >
                            <span className="sm:hidden">⛶</span>
                            <span className="hidden sm:inline">Fullscreen</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-3 sm:p-5">
                {/* Stats strip — compact, orange/stone theme */}
                {showStats && (
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                        <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
                            <p className="text-stone-500 text-xs sm:text-sm font-medium">Total</p>
                            <p className="text-xl sm:text-2xl font-bold text-stone-800">{stats.total}</p>
                        </div>
                        <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
                            <p className="text-emerald-600 text-xs sm:text-sm font-medium">Available</p>
                            <p className="text-xl sm:text-2xl font-bold text-emerald-600">{stats.available}</p>
                        </div>
                        <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
                            <p className="text-rose-600 text-xs sm:text-sm font-medium">Occupied</p>
                            <p className="text-xl sm:text-2xl font-bold text-rose-600">{stats.occupied}</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="mb-4">
                        <AlertMessage
                            message={error}
                            type="error"
                            onDismiss={() => setError(null)}
                            animate={true}
                        />
                    </div>
                )}

                {/* Search and filter — Manage Menu style */}
                <div className="mb-4 sm:mb-6">
                    <div className="relative group mb-3">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-lg group-focus-within:text-orange-500 transition-colors">🔍</span>
                        <input
                            type="text"
                            placeholder="Search tables by number..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 sm:py-2.5 bg-white border border-stone-200 rounded-2xl text-base sm:text-sm focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400 text-stone-800 placeholder:text-stone-400 transition-all shadow-sm"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {['all', 'available', 'occupied'].map((status) => (
                            <button
                                key={status}
                                onClick={() => setFilterStatus(status)}
                                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${filterStatus === status
                                    ? status === 'all' ? 'bg-orange-500 text-white shadow-md' : status === 'available' ? 'bg-emerald-500 text-white shadow-md' : 'bg-rose-500 text-white shadow-md'
                                    : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'}`}
                            >
                                {status === 'all' ? 'All Tables' : status === 'available' ? 'Available' : 'Occupied'}
                            </button>
                        ))}
                        {(searchTerm || filterStatus !== 'all') && (
                            <button onClick={() => { setSearchTerm(''); setFilterStatus('all'); }} className="text-sm font-semibold text-orange-600 hover:text-orange-700 py-2.5 px-2">Clear</button>
                        )}
                    </div>
                </div>

                {/* Add New Table */}
                <div className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-5 mb-6 shadow-sm">
                    <h3 className="text-base sm:text-lg font-bold text-stone-800 mb-3 flex items-center gap-2">
                        <span>➕</span> Add New Table
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            placeholder="Table number (e.g. T-01, Table 5)"
                            value={newTableNumber}
                            onChange={(e) => setNewTableNumber(e.target.value)}
                            className="flex-1 w-full px-4 py-2.5 border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 bg-white text-stone-800"
                            onKeyPress={(e) => e.key === 'Enter' && addTable()}
                        />
                        <button
                            onClick={addTable}
                            disabled={!newTableNumber.trim()}
                            className="sm:w-auto px-6 py-2.5 rounded-xl font-bold text-sm bg-orange-500 hover:bg-orange-600 disabled:bg-stone-300 disabled:cursor-not-allowed text-white shadow-md transition-all flex items-center justify-center gap-2"
                        >
                            Add Table
                        </button>
                    </div>
                </div>

                {/* Tables list header */}
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-stone-800">
                        Tables <span className="text-stone-500 font-normal text-sm">({filteredTables.length} of {tables.length})</span>
                    </h3>
                </div>

                {isLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="bg-stone-100 animate-pulse rounded-2xl h-40" />
                        ))}
                    </div>
                ) : filteredTables.length === 0 ? (
                    <div className="text-center py-16 rounded-2xl bg-white border-2 border-dashed border-orange-200 shadow-inner">
                        <p className="text-6xl mb-4">🪑</p>
                        <h4 className="text-lg font-bold text-stone-800 mb-2">{searchTerm || filterStatus !== 'all' ? 'No tables found' : 'No tables yet'}</h4>
                        <p className="text-stone-600 text-sm mb-5">{searchTerm || filterStatus !== 'all' ? 'Try different search or filter' : 'Add your first table to get started'}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                        {filteredTables.map((table) => (
                            <div
                                key={table._id}
                                className="bg-white rounded-xl overflow-hidden border border-stone-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-150 flex flex-col border-t-2 border-t-orange-400"
                            >
                                <div className="p-3 sm:p-4 flex-1 flex flex-col">
                                    <div className="flex items-start justify-between mb-1.5">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${table.status === 'Available' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                {table.status === 'Available' ? '✅' : '🔴'}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] uppercase tracking-wide text-stone-400">Table</p>
                                                <h4 className="text-sm sm:text-base font-bold text-stone-800 truncate">
                                                    {table.tableNumber}
                                                </h4>
                                            </div>
                                        </div>
                                        <span className={`ml-2 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${table.status === 'Available' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                                            {table.status}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-stone-500 mb-2">
                                        {table.status === 'Available' ? 'Ready to seat guests' : 'Currently occupied'}
                                    </p>
                                    <div className="mt-auto pt-2 border-t border-stone-100 space-y-1.5">
                                        <div className="grid grid-cols-2 gap-1.5">
                                            <button
                                                onClick={() => toggleTableStatus(table._id)}
                                                className={`py-1.5 rounded-lg text-[11px] font-semibold ${table.status === 'Available' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}
                                            >
                                                {table.status === 'Available' ? 'Occupy' : 'Free'}
                                            </button>
                                            <button
                                                onClick={() => fetchOrdersForTable(table._id, table.tableNumber)}
                                                className="py-1.5 rounded-lg text-[11px] font-semibold bg-orange-500 hover:bg-orange-600 text-white"
                                            >
                                                Orders
                                            </button>
                                        </div>
                                        <div className="flex gap-1.5 justify-between">
                                            <button
                                                onClick={() => openAddOrderModal(table._id)}
                                                className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 flex items-center justify-center gap-1"
                                                title="Add order"
                                            >
                                                ➕ <span className="hidden xs:inline">Order</span>
                                            </button>
                                            <button
                                                onClick={() => showTableQR(table)}
                                                className="w-9 py-1.5 rounded-lg text-[11px] font-semibold bg-stone-100 text-stone-700 hover:bg-stone-200 flex items-center justify-center"
                                                title="Show QR"
                                            >
                                                QR
                                            </button>
                                            <button
                                                onClick={() => deleteTable(table._id)}
                                                className="w-9 py-1.5 rounded-lg text-[11px] font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 flex items-center justify-center"
                                                title="Delete table"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Show Orders for Selected Table */}
                {selectedTable && showOrderDetails && (
                <div className="mt-6 p-4 sm:p-6 bg-white rounded-2xl border border-stone-200 shadow-md">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg sm:text-xl font-bold text-stone-800">Table {selectedTableNumber} — Orders</h3>
                            <p className="text-stone-500 text-sm">Manage orders for this table</p>
                        </div>
                        <button
                            onClick={closeOrderDetails}
                            className="p-2 rounded-xl bg-rose-100 text-rose-600 hover:bg-rose-200 transition-all"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Order Search */}
                    <div className="mb-4">
                        <div className="relative group">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-orange-500">🔍</span>
                            <input
                                type="text"
                                placeholder="Search by customer, mobile, or item..."
                                value={orderSearchTerm}
                                onChange={(e) => setOrderSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400 text-stone-800 text-sm"
                            />
                        </div>
                        {orderSearchTerm && (
                            <p className="text-xs text-stone-500 mt-1">Found {filteredOrders.length} order(s)</p>
                        )}
                    </div>

                    {/* Date filter */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 p-3 bg-stone-50 rounded-xl border border-stone-100">
                        <input
                            type="date"
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                            className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400"
                        />
                        <input
                            type="date"
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                            className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400"
                        />
                        <button
                            onClick={applyDateFilter}
                            className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white"
                        >
                            Apply
                        </button>
                    </div>

                    {/* Display Orders */}
                    {filteredOrders.length === 0 ? (
                        <div className="text-center py-12 rounded-xl border-2 border-dashed border-stone-200 bg-stone-50/50">
                            <p className="text-4xl mb-3">📝</p>
                            <p className="text-stone-700 font-medium">{orderSearchTerm ? 'No orders match your search' : 'No orders found'}</p>
                            <p className="text-stone-500 text-sm mt-1">{orderSearchTerm ? 'Try different search' : 'Adjust date filter or add an order'}</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {filteredOrders.map((order) => (
                                <div
                                    key={order._id}
                                    className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-all"
                                >
                                    <div className="flex flex-col lg:flex-row gap-6">
                                        <div className="flex-1">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg">👤</span>
                                                        <span className="font-semibold text-stone-800">{order.customerName}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg">📱</span>
                                                        <span className="text-stone-600">{order.mobileNumber}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg">🕐</span>
                                                        <span className="text-stone-600 text-sm">
                                                            {order.orderDate ? new Date(order.orderDate).toLocaleString() : new Date().toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Food Bill:</span>
                                                        <span className="font-semibold text-green-600">₹{order.totalAmount.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Tax ({order.taxPercentage}%):</span>
                                                        <span className="font-semibold text-blue-600">₹{order.taxAmount.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Service:</span>
                                                        <span className="font-semibold text-purple-600">₹{(order.serviceAmount && order.serviceAmount > 0 ? order.serviceAmount : 0)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                                        <span className="font-bold text-gray-800">Final Bill:</span>
                                                        <span className="font-bold text-xl text-green-600">₹{order.finalAmount.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <h5 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                                                    <span className="text-lg">🍽️</span>
                                                    Items Ordered
                                                </h5>
                                                <div className="bg-stone-50 p-3 rounded-lg space-y-2">
                                                    {order.items.map((item, itemIndex) => (
                                                        <div key={itemIndex} className="flex justify-between items-center py-1">
                                                            <span className="text-stone-700">{item.name} <span className="text-orange-600 font-semibold">(×{item.quantity})</span></span>
                                                            <span className="font-semibold text-stone-800">₹{(item.quantity * item.price).toFixed(2)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <OrderBillSender orderId={order._id} order={order} showFeedback={false} />
                                        </div>

                                        <div className="flex flex-col gap-2 min-w-[120px]">
                                            <button
                                                onClick={() => setSelectedOrderForBill(order)}
                                                className="px-3 py-2 rounded-lg text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1.5 justify-center"
                                            >
                                                📄 View Bill
                                            </button>
                                            {canEditOrder(order.orderDate || order.createdAt) ? (
                                                <button
                                                    onClick={() => handleEditOrder(order)}
                                                    className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1.5 justify-center"
                                                >
                                                    ✏️ Edit
                                                </button>
                                            ) : (
                                                <div className="px-3 py-2 rounded-lg text-xs font-bold bg-stone-200 text-stone-500 flex items-center justify-center cursor-not-allowed" title="Edit allowed within 30 min">
                                                    🔒 Edit Expired
                                                </div>
                                            )}
                                            {canDeleteOrder(order.orderDate || order.createdAt) ? (
                                                <button
                                                    onClick={() => deleteOrder(order._id)}
                                                    className="px-3 py-2 rounded-lg text-xs font-bold bg-rose-500 hover:bg-rose-600 text-white flex items-center gap-1.5 justify-center"
                                                >
                                                    🗑️ Delete
                                                </button>
                                            ) : (
                                                <div className="px-3 py-2 rounded-lg text-xs font-bold bg-stone-200 text-stone-500 flex items-center justify-center cursor-not-allowed" title="Delete allowed within 1 hour">
                                                    🔒 Delete Expired
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                )}
            </div>

            {/* Order Modal — redesigned to match Table Management */}
            {showOrderModal && (
                <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4" onClick={closeOrderModal}>
                    <div className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b-2 border-orange-400 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="text-lg">📋</span>
                                <div>
                                    <h4 className="text-base sm:text-lg font-bold text-stone-800">{isEditing ? 'Edit Order' : 'Add New Order'}</h4>
                                    <p className="text-xs text-stone-500">Customer details & items</p>
                                </div>
                            </div>
                            <button onClick={closeOrderModal} className="p-1.5 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors" aria-label="Close">×</button>
                        </div>

                        <div className="overflow-y-auto flex-1 p-4 sm:p-5">
                            {modalError && (
                                <div className="mb-4">
                                    <AlertMessage message={modalError} type="error" onDismiss={() => setModalError('')} animate={true} />
                                </div>
                            )}
                            {modalSuccess && (
                                <div className="mb-4">
                                    <AlertMessage message={modalSuccess} type="success" onDismiss={() => setModalSuccess('')} animate={false} />
                                </div>
                            )}
                            {isCreatingOrder && (
                                <div className="mb-4 bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-400 border-t-transparent flex-shrink-0" />
                                    <p className="text-orange-700 font-medium text-sm">Creating order...</p>
                                </div>
                            )}
                            {isUpdatingOrder && (
                                <div className="mb-4 bg-stone-100 border border-stone-200 rounded-xl p-3 flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-stone-600 border-t-transparent flex-shrink-0" />
                                    <p className="text-stone-700 font-medium text-sm">Updating order...</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                <div>
                                    <label className="block text-xs font-medium text-stone-500 mb-1">Customer Name *</label>
                                    <input
                                        type="text"
                                        placeholder="Enter name"
                                        value={newOrder.customerName}
                                        onChange={(e) => setNewOrder({ ...newOrder, customerName: e.target.value })}
                                        className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400 text-stone-800"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-stone-500 mb-1">Mobile (optional)</label>
                                    <input
                                        type="tel"
                                        placeholder="10 digits"
                                        maxLength={10}
                                        value={newOrder.mobileNumber}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setNewOrder({ ...newOrder, mobileNumber: value });
                                        }}
                                        className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400 text-stone-800"
                                    />
                                </div>
                            </div>

                            <p className="text-xs font-medium text-stone-500 mb-2">Items</p>
                            {newOrder.items.map((item, index) => (
                                <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-3 p-3 bg-stone-50 rounded-xl border border-stone-100">
                                    <div className="flex-1 min-w-0 relative item-dropdown-container">
                                        <input
                                            type="text"
                                            placeholder="Search & select item..."
                                            value={item.name || itemSearchTerms[index] || ''}
                                            onChange={(e) => handleItemSearchChange(e.target.value, index)}
                                            onFocus={() => setOpenDropdownIndex(index)}
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400 bg-white text-stone-800"
                                        />
                                        {openDropdownIndex === index && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-44 overflow-y-auto">
                                                {getFilteredMenuItems(index).length > 0 ? (
                                                    getFilteredMenuItems(index).map((menuItem) => {
                                                        const isAlreadySelected = newOrder.items.some(it => it.itemId === menuItem._id);
                                                        return (
                                                            <div
                                                                key={menuItem._id}
                                                                onClick={() => !isAlreadySelected && handleItemSelect(menuItem, index)}
                                                                className={`px-3 py-2.5 border-b border-stone-100 last:border-b-0 text-sm ${isAlreadySelected ? 'opacity-50 pointer-events-none select-none bg-stone-50' : 'hover:bg-orange-50 cursor-pointer'}`}
                                                            >
                                                                <div className="font-medium text-stone-800">{menuItem.itemName}</div>
                                                                <div className="text-stone-500 text-xs">₹{menuItem.itemPrice}{isAlreadySelected ? ' (already added)' : ''}</div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="px-3 py-2 text-stone-500 text-sm">No items found</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden bg-white">
                                            <button
                                                type="button"
                                                onClick={() => setItemQuantity(index, Math.max(1, (Number(item.quantity) || 1) - 1))}
                                                className="p-2 text-stone-600 hover:bg-stone-100 active:bg-stone-200 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                                disabled={(Number(item.quantity) || 1) <= 1}
                                                aria-label="Decrease quantity"
                                            >
                                                −
                                            </button>
                                            <input
                                                type="number"
                                                name="quantity"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(e, index)}
                                                className="w-12 px-1 py-2 text-sm border-0 border-x border-stone-200 text-center text-stone-800 focus:ring-0 focus:outline-none"
                                                min="1"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setItemQuantity(index, (Number(item.quantity) || 1) + 1)}
                                                className="p-2 text-stone-600 hover:bg-stone-100 active:bg-stone-200 transition-colors"
                                                aria-label="Increase quantity"
                                            >
                                                +
                                            </button>
                                        </div>
                                        <span className="text-sm font-semibold text-orange-600 min-w-[60px]">₹{(item.quantity * item.price).toFixed(2)}</span>
                                        <button
                                            onClick={() => removeItem(index)}
                                            className="p-2 rounded-lg bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors"
                                            title="Remove"
                                            aria-label="Remove item"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                onClick={addItemRow}
                                className="w-full py-2.5 rounded-xl text-sm font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-200 transition-colors mb-4"
                            >
                                + Add Item
                            </button>

                            <div className="border-t border-stone-200 pt-4 space-y-2">
                                <div className="flex justify-between text-sm text-stone-600">
                                    <span>Subtotal</span>
                                    <span className="font-medium text-stone-800">₹{(calculateTotalAmount()).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm text-stone-600">
                                    <span>Tax ({restaurantDetails?.taxPercentage ?? 18}%)</span>
                                    <span className="font-medium text-stone-800">₹{calculateTaxAmount()}</span>
                                </div>
                                <div className="flex justify-between text-sm text-stone-600">
                                    <span>Service</span>
                                    <span className="font-medium text-stone-800">₹{handleServiceAmount().toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200">
                                    <span className="text-stone-800">Total</span>
                                    <span className="text-orange-600">₹{(calculateTotalAmount() + parseFloat(calculateTaxAmount()) + handleServiceAmount()).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 p-4 sm:p-5 border-t border-stone-200 flex-shrink-0 bg-stone-50/50">
                            {isEditing ? (
                                <button
                                    onClick={updateOrder}
                                    disabled={isUpdatingOrder}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-stone-600 hover:bg-stone-700 text-white disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                                >
                                    {isUpdatingOrder && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                                    {isUpdatingOrder ? 'Updating...' : 'Update Order'}
                                </button>
                            ) : (
                                <button
                                    onClick={addOrder}
                                    disabled={isCreatingOrder}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                                >
                                    {isCreatingOrder && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                                    {isCreatingOrder ? 'Creating...' : 'Add Order'}
                                </button>
                            )}
                            <button
                                onClick={closeOrderModal}
                                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedOrderForBill && (
                <BillDetailsModal
                    order={selectedOrderForBill}
                    restaurant={restaurantDetails}
                    onClose={() => setSelectedOrderForBill(null)}
                />
            )}

            {/* QR Code Modal — redesigned to match Table Management */}
            {selectedTableForQR && (
                <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeQRModal}>
                    <div className="bg-white rounded-2xl max-w-md w-full shadow-xl border border-stone-200 overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b-2 border-orange-400 bg-white">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">📱</span>
                                <div>
                                    <h3 className="text-lg font-bold text-stone-800">Table QR Code</h3>
                                    <p className="text-xs text-stone-500">Table {selectedTableForQR.tableNumber}</p>
                                </div>
                            </div>
                            <button
                                onClick={closeQRModal}
                                className="p-2 rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-4 sm:p-6">
                            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 sm:p-5 mb-4 text-center">
                                <p className="text-sm text-stone-600 mb-3">
                                    Guests scan this code to open the digital menu
                                </p>
                                {qrCode && (
                                    <div ref={qrRef} className="bg-white p-3 rounded-xl border border-stone-200 shadow-sm inline-block">
                                        <QRCode
                                            value={qrCode}
                                            size={200}
                                            style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                            includeMargin={true}
                                        />
                                    </div>
                                )}
                            </div>

                            <p className="text-xs text-stone-500 mb-3 font-medium">Download or share</p>
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={async () => {
                                            try {
                                                // Wait longer for QR code to be ready
                                                await new Promise(resolve => setTimeout(resolve, 500));

                                                let canvas = qrRef.current?.querySelector('canvas');
                                                
                                                // If no canvas found, try to convert SVG to canvas
                                                if (!canvas) {
                                                    const svg = qrRef.current?.querySelector('svg');
                                                    if (svg) {
                                                        canvas = document.createElement('canvas');
                                                        const ctx = canvas.getContext('2d');
                                                        const img = new Image();
                                                        const svgData = new XMLSerializer().serializeToString(svg);
                                                        const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                                                        const url = URL.createObjectURL(svgBlob);
                                                        
                                                        await new Promise((resolve, reject) => {
                                                            img.onload = () => {
                                                                canvas.width = img.width || 200;
                                                                canvas.height = img.height || 200;
                                                                ctx.fillStyle = 'white';
                                                                ctx.fillRect(0, 0, canvas.width, canvas.height);
                                                                ctx.drawImage(img, 0, 0);
                                                                URL.revokeObjectURL(url);
                                                                resolve();
                                                            };
                                                            img.onerror = reject;
                                                            img.src = url;
                                                        });
                                                    }
                                                }
                                                
                                                if (canvas && canvas.width > 0 && canvas.height > 0) {
                                                    const name = `table-${selectedTableForQR.tableNumber}-qr.png`;
                                                    const result = await saveDataUrlAsFile(name, canvas.toDataURL('image/png'));
                                                    const hint = savedFileHint(result);
                                                    showToast(hint ? `PNG saved. ${hint}` : 'PNG downloaded successfully!', 'success');
                                                } else {
                                                    showToast('QR code not ready for download. Please try again.', 'error');
                                                }
                                            } catch (error) {
                                                console.error('Download error:', error);
                                                showToast('Failed to download QR code. Please try again.', 'error');
                                            }
                                        }}
                                        className="py-2.5 rounded-xl text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-all"
                                    >
                                        🖼️ PNG
                                    </button>

                                    <button
                                        onClick={async () => {
                                            try {
                                                // Wait longer for QR code to be ready
                                                await new Promise(resolve => setTimeout(resolve, 500));

                                                let canvas = qrRef.current?.querySelector('canvas');
                                                
                                                // If no canvas found, try to convert SVG to canvas
                                                if (!canvas) {
                                                    const svg = qrRef.current?.querySelector('svg');
                                                    if (svg) {
                                                        canvas = document.createElement('canvas');
                                                        const ctx = canvas.getContext('2d');
                                                        const img = new Image();
                                                        const svgData = new XMLSerializer().serializeToString(svg);
                                                        const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                                                        const url = URL.createObjectURL(svgBlob);
                                                        
                                                        await new Promise((resolve, reject) => {
                                                            img.onload = () => {
                                                                canvas.width = img.width || 200;
                                                                canvas.height = img.height || 200;
                                                                ctx.fillStyle = 'white';
                                                                ctx.fillRect(0, 0, canvas.width, canvas.height);
                                                                ctx.drawImage(img, 0, 0);
                                                                URL.revokeObjectURL(url);
                                                                resolve();
                                                            };
                                                            img.onerror = reject;
                                                            img.src = url;
                                                        });
                                                    }
                                                }
                                                
                                                if (canvas && canvas.width > 0 && canvas.height > 0) {
                                                    const name = `table-${selectedTableForQR.tableNumber}-qr.jpg`;
                                                    const result = await saveDataUrlAsFile(name, canvas.toDataURL('image/jpeg', 0.9));
                                                    const hint = savedFileHint(result);
                                                    showToast(hint ? `JPG saved. ${hint}` : 'JPG downloaded successfully!', 'success');
                                                } else {
                                                    showToast('QR code not ready for download. Please try again.', 'error');
                                                }
                                            } catch (error) {
                                                console.error('Download error:', error);
                                                showToast('Failed to download QR code. Please try again.', 'error');
                                            }
                                        }}
                                        className="py-2.5 rounded-xl text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white transition-all"
                                    >
                                        📷 JPG
                                    </button>
                                </div>

                                <button
                                    onClick={async () => {
                                        try {
                                            // Wait longer for QR code to be ready
                                            await new Promise(resolve => setTimeout(resolve, 500));

                                            let canvas = qrRef.current?.querySelector('canvas');
                                            
                                            // If no canvas found, try to convert SVG to canvas
                                            if (!canvas) {
                                                const svg = qrRef.current?.querySelector('svg');
                                                if (svg) {
                                                    canvas = document.createElement('canvas');
                                                    const ctx = canvas.getContext('2d');
                                                    const img = new Image();
                                                    const svgData = new XMLSerializer().serializeToString(svg);
                                                    const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                                                    const url = URL.createObjectURL(svgBlob);
                                                    
                                                    await new Promise((resolve, reject) => {
                                                        img.onload = () => {
                                                            canvas.width = img.width || 200;
                                                            canvas.height = img.height || 200;
                                                            ctx.fillStyle = 'white';
                                                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                                                            ctx.drawImage(img, 0, 0);
                                                            URL.revokeObjectURL(url);
                                                            resolve();
                                                        };
                                                        img.onerror = reject;
                                                        img.src = url;
                                                    });
                                                }
                                            }
                                            
                                            if (canvas && canvas.width > 0 && canvas.height > 0) {
                                                const { jsPDF } = window.jspdf;
                                                const pdf = new jsPDF();
                                                const pageWidth = pdf.internal.pageSize.getWidth();
                                                
                                                // Header - Restaurant Logo & Name
                                                pdf.setFontSize(24);
                                                pdf.setFont(undefined, 'bold');
                                                const restaurantName = restaurantDetails?.name || 'Restaurant Name';
                                                const nameWidth = pdf.getTextWidth(restaurantName);
                                                pdf.text(restaurantName, (pageWidth - nameWidth) / 2, 25);
                                                
                                                // Restaurant details in columns
                                                pdf.setFontSize(10);
                                                pdf.setFont(undefined, 'normal');
                                                const address = restaurantDetails?.address || 'Restaurant Address';
                                                const phone = restaurantDetails?.phone || 'Phone Number';
                                                const email = restaurantDetails?.email || 'Email Address';
                                                
                                                pdf.text('Address:', 20, 40);
                                                pdf.text(address, 50, 40);
                                                pdf.text('Phone:', 20, 48);
                                                pdf.text(phone, 50, 48);
                                                pdf.text('Email:', 20, 56);
                                                pdf.text(email, 50, 56);
                                                
                                                // Table info centered
                                                pdf.setFontSize(18);
                                                pdf.setFont(undefined, 'bold');
                                                const tableText = `Table ${selectedTableForQR.tableNumber}`;
                                                const tableWidth = pdf.getTextWidth(tableText);
                                                pdf.text(tableText, (pageWidth - tableWidth) / 2, 75);
                                                
                                                pdf.setFontSize(12);
                                                pdf.setFont(undefined, 'normal');
                                                const instructionText = 'Scan this QR code to access our digital menu';
                                                const instructionWidth = pdf.getTextWidth(instructionText);
                                                pdf.text(instructionText, (pageWidth - instructionWidth) / 2, 85);
                                                
                                                // QR code centered
                                                const imgData = canvas.toDataURL('image/png');
                                                const qrSize = 80;
                                                pdf.addImage(imgData, 'PNG', (pageWidth - qrSize) / 2, 95, qrSize, qrSize);
                                                
                                                // Instructions
                                                pdf.setFontSize(10);
                                                pdf.text('Instructions:', 20, 190);
                                                pdf.text('1. Open camera app on your phone', 30, 200);
                                                pdf.text('2. Point camera at QR code', 30, 208);
                                                pdf.text('3. Tap notification to open menu', 30, 216);
                                                pdf.text('4. Browse and order directly from your phone', 30, 224);
                                                
                                                // Footer - Company branding
                                                pdf.setFontSize(8);
                                                pdf.setFont(undefined, 'italic');
                                                const footerText = 'Powered by RestroAI.in - Smart Restaurant Management';
                                                const footerWidth = pdf.getTextWidth(footerText);
                                                pdf.text(footerText, (pageWidth - footerWidth) / 2, 270);
                                                
                                                const dateText = `Generated on ${new Date().toLocaleDateString()}`;
                                                const dateWidth = pdf.getTextWidth(dateText);
                                                pdf.text(dateText, (pageWidth - dateWidth) / 2, 280);
                                                
                                                const pdfName = `table-${selectedTableForQR.tableNumber}-qr.pdf`;
                                                const pdfDataUrl = pdf.output('datauristring');
                                                const result = await saveDataUrlAsFile(pdfName, pdfDataUrl);
                                                const hint = savedFileHint(result);
                                                showToast(hint ? `PDF saved. ${hint}` : 'PDF downloaded successfully!', 'success');
                                            } else {
                                                showToast('QR code not ready for download. Please try again.', 'error');
                                            }
                                        } catch (error) {
                                            console.error('PDF Download error:', error);
                                            showToast('Failed to download PDF. Please try again.', 'error');
                                        }
                                    }}
                                    className="w-full py-2.5 rounded-xl text-sm font-semibold bg-stone-700 hover:bg-stone-800 text-white transition-all"
                                >
                                    📄 Download PDF
                                </button>

                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(qrCode);
                                                showToast('Table URL copied to clipboard!', 'success');
                                            } catch (error) {
                                                const textArea = document.createElement('textarea');
                                                textArea.value = qrCode;
                                                document.body.appendChild(textArea);
                                                textArea.select();
                                                document.execCommand('copy');
                                                document.body.removeChild(textArea);
                                                showToast('Table URL copied to clipboard!', 'success');
                                            }
                                        }}
                                        className="py-2.5 rounded-xl text-sm font-semibold bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200 transition-all"
                                    >
                                        🔗 Copy URL
                                    </button>

                                    <button
                                        onClick={async () => {
                                            try {
                                                if (navigator.share && navigator.canShare) {
                                                    await navigator.share({
                                                        title: `Table ${selectedTableForQR.tableNumber} - ${restaurantDetails?.name || 'Restaurant'}`,
                                                        text: 'Scan this QR code to access our digital menu',
                                                        url: qrCode
                                                    });
                                                } else {
                                                    await navigator.clipboard.writeText(qrCode);
                                                    showToast('QR code link copied to clipboard!', 'success');
                                                }
                                            } catch (error) {
                                                console.error('Share error:', error);
                                                showToast('Failed to share QR code. Please try again.', 'error');
                                            }
                                        }}
                                        className="py-2.5 rounded-xl text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white transition-all"
                                    >
                                        📤 Share
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => removeToast(toast.id)}
                />
            ))}

            <style>{`
                @keyframes fade-in-up {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default TableStatus;
